import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AlertService, PausableObservableDataLoader } from '@bsquare/angular-common';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { BehaviorSubject, defer, Observable, ReplaySubject, timer } from 'rxjs';
import { delayWhen, retryWhen, take, tap } from 'rxjs/operators';
import { PermissionService } from './user-management/permission.service';

export const DEFAULT_ROUTE = '/manage';
const OIDC_CONFIG_FILE = '/oidc-config.json';
const REAUTH_REDIRECT_TIMEOUT = 3000;

// Need a higher than default leak threshold there are a lot of permissions
// subscriptions.
const PERMISSIONS_LEAK_THRESHOLD = 150;

const AUTH_CONFIG: AuthConfig = {
  // URL of the SPA to redirect the user to after login
  redirectUri: window.location.origin,
  postLogoutRedirectUri: window.location.origin,
  silentRefreshRedirectUri: `${window.location.origin}/assets/silent-refresh.html`,
  useSilentRefresh: true,

  // set the scope for the permissions the client should request
  // The first three are defined by OIDC.
  scope: 'openid email',
  responseType: 'id_token token',

  strictDiscoveryDocumentValidation: false,
  // Otherwise router.navigate doesn't work.
  clearHashAfterLogin: false,
};

export interface UserInfo {
  aud: string;
  exp: number;
  iat: number;
  sub: string;
  email?: string;
  username: string;
  preferred_username?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
}

export interface PartialOidcConfig {
  issuer: string;
  clientId: string;
  logoutUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // This only exists because there is a bug in angular
  // https://github.com/angular/angular/issues/25590
  public static readonly logout = new ReplaySubject<boolean>(1);

  public readonly userInfo = new ReplaySubject<UserInfo>(1);

  public readonly loggedIn = new BehaviorSubject<boolean>(false);

  public get permissions(): Observable<string[]> {
    // Pausable observable data loader clears the cache on error. Since this function is called so often
    // (including on view checked which occurs when the angular httpClient errors), we want to delay any new requests
    // for 30s after an error occurs by pausing it.
    return defer(() => this.userPermissionsSubject.load('')).pipe(
      retryWhen((errors) =>
        errors.pipe(
          // pausing and resuming tells new load instances for this observableDataLoader to wait for the next 30 seconds.
          tap(() => this.userPermissionsSubject.pause()),
          delayWhen(() => timer(30000)),
          tap(() => this.userPermissionsSubject.resume()),
        ),
      ),
    );
  }

  private readonly oauthService: OAuthService;
  private readonly router: Router;
  private readonly httpClient: HttpClient;
  private redirect?: string;
  private state: 'CONFIGURED' | 'CONFIGURING' | 'IDLE' = 'IDLE';
  private readonly alertService: AlertService;
  private readonly zone: NgZone;
  private readonly permissionService: PermissionService;
  // Start Paused to avoid permissions being loaded before authentication occurs
  private readonly userPermissionsSubject = new PausableObservableDataLoader<string[]>(
    () => {
      return this.permissionService.userPermissions.pipe(take(1));
    },
    true,
    {
      // Used a lot
      leakThreshold: PERMISSIONS_LEAK_THRESHOLD,
      // Assume permissions do not change over a users session.
      // Enabling this can cause list view configs to reload and whole
      // portions of the UI to reload every 30s or s.
      reloadOnExpire: false,
    },
  );

  public constructor() {
    const oauthService = inject(OAuthService);
    const router = inject(Router);
    const httpClient = inject(HttpClient);
    const alertService = inject(AlertService);
    const zone = inject(NgZone);
    const permissionService = inject(PermissionService);

    this.oauthService = oauthService;
    this.router = router;
    this.httpClient = httpClient;
    this.alertService = alertService;
    this.zone = zone;
    this.permissionService = permissionService;

    AuthService.logout.subscribe((value) => {
      if (value) {
        AuthService.logout.next(false);
        this.logout();
      }
    });

    this.oauthService.events.subscribe((event) => {
      console.debug('OIDC Event', event);
      // Events aren't always fired from within a zone. Sometimes the lib uses setInterval etc.
      this.zone.run(() => {
        switch (event.type) {
          case 'session_terminated':
            this.logout();
            break;
          case 'token_error': // Fallthrough.
          case 'invalid_nonce_in_state': // Fallthrough.
          case 'token_validation_error':
            this.alertService.show({
              type: 'error',
              message:
                'There was an error with your token. You will be redirected to the login screen.',
            });
            this.reauth();
            break;
          case 'silent_refresh_error': // Fallthrough.
          case 'silent_refresh_timeout':
            this.alertService.show({
              type: 'warning',
              message:
                'We failed to refresh your login credentials automatically. You will be redirected to the login screen.',
            });
            this.reauth();
            break;
          case 'token_received':
            // Set a cookie to allow download links to work.
            document.cookie = `Authorization=Bearer ${this.oauthService.getAccessToken()}; path=/api`;
            break;
          default:
            break;
        }
      });
    });

    this.updateLoggedIn();
    this.storeRoute();
    this.configure();
  }

  public storeRoute(): void {
    this.redirect = this.getRoute();
    console.debug('Stored redirect', this.redirect);
  }

  public configure(): boolean {
    // If it's a silent refresh then redirect it to the silent refresh page.
    // Note that IE11 doesn't support URLSearchParams and the polyfill seems
    // to break authentication.
    const rawParams = window.location.href.split('?');
    // If there's anything after the ?
    if (rawParams.length > 1) {
      // Split them into individual key value pairs
      const params = rawParams[1].split('&').map((param) => {
        const [key, value] = param.split('=');
        return {
          key: decodeURIComponent(key),
          value: decodeURIComponent(value),
        };
      });
      // Find if any of the key value pairs contain redirect_uri
      const redirectUri = params.find((param) => param.key === 'redirect_uri');
      // If the redirect_uri is the silent refresh page, then redirect.
      if (redirectUri && redirectUri.value === AUTH_CONFIG.silentRefreshRedirectUri) {
        console.debug('Redirecting to silent refresh frame');
        window.location.assign(AUTH_CONFIG.silentRefreshRedirectUri);
        return true;
      }
    }

    if (this.state !== 'IDLE') {
      return true;
    }
    this.state = 'CONFIGURING';

    this.httpClient.get<PartialOidcConfig>(OIDC_CONFIG_FILE).subscribe(
      (config) => {
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        const authConfig: AuthConfig = { ...AUTH_CONFIG, ...config };

        if (!authConfig.issuer || !authConfig.clientId) {
          console.error(`Invalid ${OIDC_CONFIG_FILE}. Missing issuer or client.`, authConfig);
          this.alertService.show({
            type: 'error',
            message: 'Invalid OIDC config file. Missing issuer or client',
          });
          return;
        }

        this.oauthService.configure(authConfig);
        this.oauthService.tokenValidationHandler = new JwksValidationHandler();

        this.oauthService
          .loadDiscoveryDocumentAndTryLogin()
          .then(() => {
            if (this.isLoggedIn()) {
              this.oauthService.setupAutomaticSilentRefresh();
              this.state = 'CONFIGURED';
              console.debug('Discovery complete');
              // Set a cookie to allow download links to work.
              document.cookie = `Authorization=Bearer ${this.oauthService.getAccessToken()}; path=/api`;
              // Fetch the user info.
              this.updateUserInfo();
              this.redirectRoute();
            } else {
              this.oauthService.initLoginFlow(this.redirect || this.getRoute());
            }
          })
          .catch((err: unknown) => {
            console.error('Error during login.', err);
            this.alertService.show({
              type: 'error',
              message: 'An error occurred during login. You will be redirected to try again.',
            });
            this.reauth();
          });
      },
      (error) => {
        console.error('Error fetching OIDC config', error);
        this.alertService.show({ type: 'error', message: 'Error fetching OIDC config' });
      },
    );

    return false;
  }

  public updateUserInfo(): void {
    this.oauthService
      .loadUserProfile()
      .then((userInfo) => {
        console.debug('Loaded user info', userInfo);
        this.userInfo.next(userInfo as UserInfo);

        // Update the permissions list
        this.userPermissionsSubject.reloadAll();
        // Unpause all retrieval of permissions now that auth has been updated
        this.userPermissionsSubject.resume();
      })
      .catch((err: unknown) => {
        console.error('Failed to load user info', err);
        this.alertService.show({ type: 'error', message: 'Failed to load user info' });
      });
  }

  public isLoggedIn(): boolean {
    const loggedIn = this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken();
    if (!loggedIn && this.state === 'CONFIGURED') {
      this.state = 'IDLE';
    }
    return loggedIn;
  }

  public logout(): void {
    // Reset the cookie used for download links.
    document.cookie = '';
    this.oauthService.logOut();
    // Clear the session storage to reset any column configs.
    sessionStorage.clear();
    this.state = 'IDLE';
    this.redirect = undefined;
    this.router.navigate(['/']).catch((err: unknown) => {
      this.alertService.show({
        type: 'error',
        message: 'Redirecting to home page failed. Please reload',
      });
      console.error('Redirect failed', err);
    });
    this.updateLoggedIn();
  }

  private reauth(): void {
    console.debug('relogin clearing session');
    this.storeRoute();
    // Reset the session.
    document.cookie = '';
    // Clear the session without redirecting to logout.
    this.oauthService.logOut(true);
    this.state = 'IDLE';
    // Clear any possible auth responses in the header.
    window.location.hash = '';

    timer(REAUTH_REDIRECT_TIMEOUT).subscribe(() => {
      console.debug('relogin triggering login');
      // Trigger login again.
      this.configure();
    });
  }

  private redirectRoute(): void {
    const url = this.redirect || this.oauthService.state || DEFAULT_ROUTE;
    console.debug(`Redirecting to ${url}`, this.redirect, this.oauthService.state);
    this.router.navigateByUrl(url).catch((err: unknown) => {
      this.alertService.show({ type: 'error', message: 'Redirecting failed. Please reload' });
      console.error('Redirect failed', err);
    });
    this.updateLoggedIn();
  }

  private getRoute(): string | undefined {
    const url = window.location.pathname;
    if (url && url !== '/' && !url.startsWith('/#id_token') && !url.startsWith('/?')) {
      return url;
    }
    return undefined;
  }

  private updateLoggedIn(): void {
    this.loggedIn.next(this.isLoggedIn());
  }
}
